<template>
    <v-app>
        <v-navigation-drawer v-model="drawer" 
        class="z-40"
        style="position: fixed; top: 60px; left: 0; height: 100vh; overflow-y: auto; overflow-x: hidden;">
        
            <!-- Header -->
            <v-list-item class="px-4 py-3">
                <template v-slot:prepend>
                    <v-icon color="primary" size="large">mdi-cloud</v-icon>
                </template>

                <v-list-item-title class="font-weight-semibold">
                    My Drive
                </v-list-item-title>

                <v-list-item-subtitle class="text-caption">
                    Personal • 5.3 GB used
                </v-list-item-subtitle>
            </v-list-item>

            <!-- New Button -->
            <div class="px-3 mb-4">
                <v-menu offset-y>
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props" color="primary" block prepend-icon="mdi-plus">
                            New
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item @click="addUpdateFolder">
                            <template v-slot:prepend>
                                <v-icon>mdi-folder-plus</v-icon>
                            </template>

                            <v-list-item-title>New Folder</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="triggerFileUpload">
                            <template v-slot:prepend>
                                <v-icon>mdi-upload</v-icon>
                            </template>

                            <v-list-item-title>Upload File</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <input
                ref="fileInput"
                type="file"
                multiple
                accept="*/*"
                style="display: none"
                @change="handleMultipleUpload"
                />
            </div>

            <!-- Quick Access -->
            <v-list density="compact" class="px-2">
                
                <v-list-item 
                v-for="(item, idx) in sidebarListData" 
                :key="idx" 
                rounded="lg"
                :value="item.value"
                :active="activeMenu === item.value"
                @click="handleMenuChange(item.value)"
                >
                    <template v-slot:prepend>
                        <v-icon>{{ item.icon }}</v-icon>
                    </template>
                    
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>

            </v-list>
        </v-navigation-drawer>

        <v-app-bar elevation="1" style="position: fixed; top: 60px; right: 0;">
            <!-- Breadcrumb -->
            <v-breadcrumbs :items="breadcrumbs" class="px-2">
                <template v-slot:divider class="px-0">
                    <v-icon style="font-size: 13px;">mdi-chevron-right</v-icon>
                </template>

                <template v-slot:item="{ item }">
                    <v-breadcrumbs-item
                    :disabled="item.disabled"
                    @click="handleBreadcrumbClick(item)"
                    class="cursor-pointer px-0 text-subtitle-4"
                    >
                        <v-chip style="font-size: 13px;">
                            {{ item.title }}
                        </v-chip>
                    </v-breadcrumbs-item>
                </template>
            </v-breadcrumbs>

            <v-spacer />
            
            <!-- Search -->
            <v-text-field
            ref="searchField"
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            placeholder="Search in Drive"
            variant="outlined"
            density="compact"
            hide-details
            class="search-field mx-4"
            :style="isSearchFieldExpanded ? 'max-width: 200px' : 'max-width: 44px'"
            @click="searchExpand"
            @blur="isSearchFieldExpanded = false"
            />

            <!-- Sort -->
            <v-select
            v-model="sortBy"
            :items="sortOptions"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 180px;"
            class="mr-2 small-input"
            />

            <!-- View Toggle -->
            <v-btn-toggle class="view-toggle-small" v-model="viewMode" mandatory>
                <v-btn icon="mdi-view-grid" value="grid" class="view-toggle-small" />
                <v-btn icon="mdi-view-list" value="list" class="view-toggle-small" />
            </v-btn-toggle>
        </v-app-bar>

        <!-- DOCUMENT PAGES  -->
        
        <documents-my-drive 
        :uploadProgress="uploadProgress"
        :isUploading="isUploading"
        :dragOver="dragOver"
        :triggerFileUpload="triggerFileUpload"
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :visibleItems="visibleItems"
        :visibleFolders="visibleFolders"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :openFolder="openFolder"
        :handleDrop="handleDrop"
        :openFile="openFile"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <documents-recent
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :dragOver="dragOver"
        :visibleItems="visibleItems"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :openFolder="openFolder"
        :openFile="openFile"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <documents-starred
        :uploadProgress="uploadProgress"
        :isUploading="isUploading"
        :dragOver="dragOver"
        :triggerFileUpload="triggerFileUpload"
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :visibleItems="visibleItems"
        :visibleFolders="visibleFolders"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :openFolder="openFolder"
        :handleDrop="handleDrop"
        :openFile="openFile"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <documents-archived
        :uploadProgress="uploadProgress"
        :isUploading="isUploading"
        :dragOver="dragOver"
        :triggerFileUpload="triggerFileUpload"
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :visibleItems="visibleItems"
        :visibleFolders="visibleFolders"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :openFolder="openFolder"
        :handleDrop="handleDrop"
        :openFile="openFile"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <documents-trash
        :uploadProgress="uploadProgress"
        :isUploading="isUploading"
        :dragOver="dragOver"
        :triggerFileUpload="triggerFileUpload"
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :visibleItems="visibleItems"
        :visibleFolders="visibleFolders"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :handleDrop="handleDrop"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <documents-shared
        :uploadProgress="uploadProgress"
        :isUploading="isUploading"
        :dragOver="dragOver"
        :triggerFileUpload="triggerFileUpload"
        :activeMenu="activeMenu"
        :selectedItems="selectedItems"
        :visibleItems="visibleItems"
        :visibleFolders="visibleFolders"
        :visibleFiles="visibleFiles"
        :showFolderContextMenu="showFolderContextMenu"
        :openFolder="openFolder"
        :handleDrop="handleDrop"
        :openFile="openFile"
        :isImageFile="isImageFile"
        :getFilePreviewUrl="getFilePreviewUrl"
        :getFileIconColor="getFileIconColor"
        :getFileIcon="getFileIcon"
        :showFileContextMenu="showFileContextMenu"
        :handleItemClick="handleItemClick"
        :showListContextMenu="showListContextMenu"
        :toggleSelect="toggleSelect"
        :formattedDate="formattedDate"
        :formatSize="formatSize"
        :viewMode="viewMode"
        />

        <!-- Preview Drawer -->
        <v-navigation-drawer
        v-model="previewOpen"
        location="right"
        width="450"
        temporary
        >
            <v-toolbar flat border-b>
                <v-toolbar-title>File Preview</v-toolbar-title>
                <v-spacer />
                <v-btn icon="mdi-close" @click="previewOpen = false" />
            </v-toolbar>

            <v-container v-if="previewFile" class="py-4">
                <!-- Preview Section -->
                <div class="mb-6">
                    <!-- Image Preview -->
                    <v-img
                    v-if="isImageFile(previewFile)"
                    :src="getFilePreviewUrl(previewFile)"
                    class="rounded-lg mb-4 border"
                    />

                    <!-- PDF Preview -->
                    <v-card
                    v-else-if="isPdfFile(previewFile)"
                    flat
                    color="grey-lighten-4"
                    class="mb-4 d-flex align-center justify-center"
                    style="height: 250px;"
                    >
                        <div class="text-center">
                            <v-icon size="80" color="red" class="mb-2">mdi-file-pdf-box</v-icon>
                            <div class="text-subtitle-2 font-weight-medium">PDF Document</div>
                            <div class="text-caption text-medium-emphasis">{{ previewFile.original_name }}</div>
                        </div>
                    </v-card>

                    <!-- Document Preview -->
                    <v-card
                    v-else-if="isDocumentFile(previewFile)"
                    flat
                    color="grey-lighten-4"
                    class="mb-4 d-flex align-center justify-center"
                    style="height: 250px;"
                    >
                        <div class="text-center">
                            <v-icon size="80" color="blue" class="mb-2">mdi-file-document</v-icon>
                            <div class="text-subtitle-2 font-weight-medium">Document</div>
                            <div class="text-caption text-medium-emphasis">
                                {{ getFileTypeName(previewFile) }}
                            </div>
                        </div>
                    </v-card>

                    <!-- Video Preview -->
                    <v-card
                    v-else-if="isVideoFile(previewFile)"
                    flat
                    color="grey-lighten-4"
                    class="mb-4 d-flex align-center justify-center"
                    style="height: 250px;"
                    >
                        <div class="text-center">
                            <v-icon size="80" color="purple" class="mb-2">mdi-video</v-icon>
                            <div class="text-subtitle-2 font-weight-medium">Video File</div>
                            <div class="text-caption text-medium-emphasis">
                                {{ getFileTypeName(previewFile) }}
                            </div>
                        </div>
                    </v-card>

                    <!-- Audio Preview -->
                    <v-card
                    v-else-if="isAudioFile(previewFile)"
                    flat
                    color="grey-lighten-4"
                    class="mb-4 d-flex align-center justify-center"
                    style="height: 250px;"
                    >
                        <div class="text-center">
                            <v-icon size="80" color="green" class="mb-2">mdi-music</v-icon>
                            <div class="text-subtitle-2 font-weight-medium">Audio File</div>
                            <div class="text-caption text-medium-emphasis">{{ getFileTypeName(previewFile) }}</div>
                        </div>
                    </v-card>

                    <!-- Generic File Preview -->
                    <v-card v-else flat color="grey-lighten-4" class="mb-4 d-flex align-center justify-center" style="height: 250px;">
                        <div class="text-center">
                            <v-icon size="80" color="grey" class="mb-2">mdi-file</v-icon>
                            <div class="text-subtitle-2 font-weight-medium">File</div>
                            <div class="text-caption text-medium-emphasis">{{ getFileTypeName(previewFile) }}</div>
                        </div>
                    </v-card>
                </div>

                <!-- File Information -->
                <div class="mb-4">
                    <div class="text-subtitle-2 font-weight-semibold mb-3">File Information</div>
                    
                    <v-list density="compact" class="bg-grey-lighten-4 rounded">
                        <v-list-item>
                            <v-list-item-title class="text-caption font-weight-medium">File Name</v-list-item-title>
                            <v-list-item-subtitle class="text-truncate">
                                {{ previewFile.original_name }}
                            </v-list-item-subtitle>
                        </v-list-item>

                        <v-divider />

                        <v-list-item>
                            <v-list-item-title class="text-caption font-weight-medium">File Type</v-list-item-title>
                            <v-list-item-subtitle>
                                {{ getFileTypeName(previewFile) }}
                            </v-list-item-subtitle>
                        </v-list-item>

                        <v-divider />

                        <v-list-item>
                            <v-list-item-title class="text-caption font-weight-medium">File Size</v-list-item-title>
                            <v-list-item-subtitle>
                                {{ formatSize(previewFile.size || 0) }}
                            </v-list-item-subtitle>
                        </v-list-item>

                        <v-divider />

                        <v-list-item>
                            <v-list-item-title class="text-caption font-weight-medium">Created Date</v-list-item-title>
                            <v-list-item-subtitle>
                                {{ formatFullDate(previewFile.created_at) }}
                            </v-list-item-subtitle>
                        </v-list-item>

                    </v-list>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex ga-2 mt-4">
                    <v-btn block variant="outlined" @click="download(previewFile)">
                        <v-icon start>mdi-download</v-icon>
                        Download
                    </v-btn>
                </div>

                <div class="d-flex ga-2 mt-2">
                    <v-btn block variant="text" @click="deleteItem(previewFile)" color="error">
                        <v-icon start>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </div>
            </v-container>
        </v-navigation-drawer>

        <!-- Folder Context Menu -->
        <v-menu
        v-model="folderContextMenu"
        :style="{ left: menuX + 'px', top: menuY + 'px' }"
        absolute
        >
            <v-list density="compact">
                <v-list-item v-if="activeMenu!=='trash'" @click="handleFolderContextAction('rename')">
                    <v-list-item-title>
                        <v-icon>mdi-fountain-pen-tip</v-icon>
                        Rename
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!=='trash'" @click="handleFolderContextAction('download')">
                    <v-list-item-title>
                        <v-icon>mdi-download</v-icon>
                        download
                    </v-list-item-title>
                </v-list-item>

                <v-list-item  v-if="activeMenu !== 'archive' && activeMenu !== 'trash'" @click="handleFolderContextAction('share')">
                    <v-list-item-title>
                        <v-icon>mdi-share-variant</v-icon>
                        Share
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu === 'trash'" @click="handleFolderContextAction('restore')">
                    <v-list-item-title>
                        <v-icon>mdi-restore</v-icon>
                        Restore 
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu === 'trash'" @click="handleFolderContextAction('delete-forever')">
                    <v-list-item-title>
                        <v-icon>mdi-delete-forever</v-icon>
                        Delete forever 
                    </v-list-item-title>
                </v-list-item>

                
                <v-list-item v-if="activeMenu !== 'archive' && activeMenu !== 'trash'" @click="handleFolderContextAction('starred')">
                    <v-list-item-title>
                        <v-icon>
                        {{ 
                            folderContextTarget.is_starred
                            ? "mdi-star-remove" 
                            : "mdi-star" 
                        }}
                        </v-icon>
                        
                        {{ 
                            folderContextTarget.is_starred
                            ? "Remove From Starred" 
                            : "Add to Starred" 
                        }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash'" @click="handleFolderContextAction('archive')">
                    <v-list-item-title>
                        <v-icon>
                            {{ 
                            folderContextTarget.is_archived === 1
                            ? "mdi-archive-cancel" 
                            : "mdi-archive" 
                        }}
                        </v-icon>
                        
                        {{ 
                            folderContextTarget.is_archived === 1
                            ? "Restore From Archive" 
                            : "Add to Archive" 
                        }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash'" @click="handleFolderContextAction('delete')">
                    <v-list-item-title>
                        <v-icon>mdi-trash-can</v-icon>
                        Move to trash
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- File Context Menu -->
        <v-menu
        v-model="fileContextMenu"
        :style="{ left: menuX + 'px', top: menuY + 'px' }"
        absolute
        >
            <v-list density="compact">
                <v-list-item v-if="activeMenu!== 'trash'" @click="handleFileContextAction('preview')">
                    <v-list-item-title>
                        <v-icon>mdi-eye-outline</v-icon>
                        Preview
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash'" @click="handleFileContextAction('download')">
                    <v-list-item-title>
                        <v-icon>mdi-download</v-icon>
                        Download
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash' && activeMenu !== 'archive' " @click="handleFileContextAction('share')">
                    <v-list-item-title>
                        <v-icon>mdi-share-variant</v-icon>
                        Share
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash' && activeMenu !== 'archive' " @click="handleFileContextAction('starred')">
                    <v-list-item-title>
                        <v-icon>
                            {{ 
                            fileContextTarget.is_starred
                            ? "mdi-star-remove" 
                            : "mdi-star" 
                        }}
                        </v-icon>
                        
                        {{ 
                            fileContextTarget.is_starred
                            ? "Remove From Starred" 
                            : "Add to Starred" 
                        }}
                    </v-list-item-title>
                    
                </v-list-item>

                
                <v-list-item v-if="activeMenu === 'trash'" @click="handleFileContextAction('restore')">
                    <v-list-item-title>
                        <v-icon>mdi-restore</v-icon>
                        Restore 
                    </v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="activeMenu === 'trash'" @click="handleFileContextAction('delete-forever')">
                    <v-list-item-title>
                        <v-icon>mdi-delete-forever</v-icon>
                        Delete forever 
                    </v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="activeMenu!== 'trash'" @click="handleFileContextAction('archive')">
                    <v-list-item-title>
                        <v-icon>
                            {{ 
                            fileContextTarget.is_archived === 1
                            ? "mdi-archive-cancel" 
                            : "mdi-archive" 
                        }}
                        </v-icon>
                        
                        {{ 
                            fileContextTarget.is_archived === 1
                            ? "Restore From Archive" 
                            : "Add to Archive" 
                        }}
                    </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="activeMenu!== 'trash'"  @click="handleFileContextAction('delete')">
                    <v-list-item-title>
                        <v-icon>mdi-trash-can-outline</v-icon>
                        Move to trash
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Rename/Update Dialog -->
        <v-dialog v-model="addUpdateDialog" max-width="400">
            <v-card>
                <v-card-title class="pb-6">{{ isUpdateMode ? 'Rename' : 'Create Folder' }}</v-card-title>
                
                <v-card-text class="py-0 px-4">
                    <v-text-field
                    v-model="name.value.value"
                    variant="outlined"
                    density="compact"
                    :label="isUpdateMode ? 'New Name' : 'Folder Name'"
                    autofocus
                    @keyup.enter="confirmSaveUpdate"
                    />
                </v-card-text>
                
                <v-card-actions class="px-4">
                    <v-spacer />
                    <v-btn class="bg-red" @click="addUpdateDialog = false">Cancel</v-btn>
                    <v-btn class="bg-primary" @click="confirmSaveUpdate">
                        {{ isUpdateMode ? 'Rename' : 'Create' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- shared with dialog -->
        <v-dialog v-model="sharedWithDialog" max-width="600">
            <v-card :loading="sharedUsersListLoading" class="pa-2">
            <!-- Title -->
                <v-card-title class="font-weight-bold text-h5">Share</v-card-title>
                <!-- File Name -->
                <div class="px-4 pb-4 text-subititle-1 text-grey">{{ sharedDialogTitle }}</div>

                <v-card-text class="pb-0">
                    <!-- USER INPUT WITH CHIPS + AVATARS -->
                    <v-row>            
                        <v-col class="px-1" cols="12" md="9">
                            <v-autocomplete
                            label="Select Users"
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            v-model="shared_with.value.value"
                            :items="usersToShare"
                            :item-title="(item) => `${item.full_name}`"
                            item-value="id"
                            @update:modelValue=""
                            autocomplete="off"
                            :loading="usersToShareListLoading"
                            multiple
                            chips
                            clearable
                            ></v-autocomplete>
                        </v-col>

                        <v-col class="px-1" cols="12" md="3">
                            <v-autocomplete
                            variant="outlined"
                            density="compact"
                            bg-color="grey-lighten-4"
                            v-model="permission.value.value"
                            :items="permissionOptions"
                            :item-title="(item) => `${item.title}`"
                            item-value="value"
                            autocomplete="off"
                            ></v-autocomplete>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-title class="font-weight-semibold text-subtitle-1 pt-0">
                    People with access
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-list lines="one">
                        <v-list-item
                            v-for="(user, idx) in sharedUsers"
                            :key="idx"
                            :title="user.name"
                            :subtitle="user.email"
                        >
                            <template #prepend>
                                <v-avatar class="me-4" size="40">
                                    <v-img 
                                    src="https://scontent.fdac174-1.fna.fbcdn.net/v/t39.30808-6/484075275_2136101626851064_2719843251563425571_n.jpg?_nc_cat=105&ccb=1-7&_nc_sid=6ee11a&_nc_eui2=AeFGy84KbWBWkOrJqlxdVCNye7J0jPHczol7snSM8dzOiWAODYbG_U42ceIHRxgV3ThCLMWx59cPG5L0sahepx8l&_nc_ohc=aUMFH3f_e0cQ7kNvwGkjCsF&_nc_oc=AdmKgfk6Wg8PxkWeUcr1vIdSdDswU-bSyos8_B0ZHQ6COaoxtT5C8QIDbL6Tgpe8f24&_nc_zt=23&_nc_ht=scontent.fdac174-1.fna&_nc_gid=wMBuHREjaVy5GQkV_tyjGA&oh=00_AfrXX5GsAABUKH-kmPrYDEDctSfQsix69MNLSF2yOSagzg&oe=6963D9CE" />
                                </v-avatar>
                            </template>

                            <template #append>
                                <v-autocomplete
                                    class="permission-select"
                                    density="compact"
                                    variant="outlined"
                                    bg-color="grey-lighten-4"
                                    v-model="user.permission"
                                    :items="getPermissionList"
                                    item-title="title"
                                    item-value="value"
                                    autocomplete="off"
                                />
                            </template>


                        </v-list-item>
                    </v-list>
                </v-card-text>

                <v-card-actions class="px-4 pb-4">
                    <v-spacer />
                    <v-btn variant="text" @click="sharedWithDialog = false">Cancel</v-btn>
                    <v-btn class="bg-primary" @click="shareWithSubmit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Upload Progress Dialog (Google Drive Style) -->
        <!-- Add this class to your v-dialog -->
        <v-dialog 
        v-model="uploadDialogOpen" 
        max-width="450"
        persistent
        :close-on-back="false"
        :scrim="false"
        class="upload-dialog-bottom-right"
        >
            <v-card>
                <!-- Header -->
                <v-toolbar flat class="border-b">
                    <v-toolbar-title class="text-h6">Uploading {{ totalFilesCount }} items</v-toolbar-title>
                    <v-spacer />
                    <v-btn 
                    icon="mdi-close" 
                    @click="uploadDialogOpen = false"
                    :disabled="isUploading"
                    />
                </v-toolbar>

                <!-- Upload Summary -->
                <v-card-text class="pa-4">
                    <!-- Overall Progress -->
                    <div class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-2">
                            <span class="text-body-2">{{ uploadedCount }} of {{ totalFilesCount }} items</span>
                            <span class="text-body-2 font-weight-bold">{{ Math.round(overallProgress) }}%</span>
                        </div>

                        <v-progress-linear
                        :model-value="overallProgress"
                        height="8"
                        rounded
                        color="primary"
                        />
                    </div>

                    <!-- Time Left & Speed -->
                    <div class="d-flex justify-space-between text-caption text-medium-emphasis mb-4">
                        <span>{{ timeLeftText }}</span>
                        <span>{{ uploadSpeedText }}</span>
                    </div>

                    <!-- Individual File Progress -->
                    <div class="file-uploads-container" style="max-height: 300px; overflow-y: auto;">
                        <div 
                        v-for="(file, index) in uploadingFiles" 
                        :key="file.id"
                        class="file-progress-item mb-3"
                        >
                            <!-- File Name & Status -->
                            <div class="d-flex align-center justify-space-between mb-2">
                                <div class="d-flex align-center ga-2" style="flex: 1; min-width: 0;">
                                    <v-icon size="20">{{ getFileIcon(file) }}</v-icon>
                                    <span class="text-body-2 text-truncate">{{ file.name }}</span>
                                </div>
                                
                                <!-- Status Icon -->
                                <v-icon 
                                v-if="file.status === 'completed'" 
                                size="20" 
                                color="success"
                                class="ml-2"
                                >
                                    mdi-check-circle
                                </v-icon>

                                <v-progress-circular 
                                v-else-if="file.status === 'uploading'" 
                                :model-value="file.progress"
                                :size="20"
                                :width="2"
                                color="primary"
                                class="ml-2"
                                />

                                <v-icon 
                                v-else-if="file.status === 'error'" 
                                size="20" 
                                color="error"
                                class="ml-2"
                                >
                                    mdi-alert-circle
                                </v-icon>
                            </div>

                            <!-- Progress Bar -->
                            <v-progress-linear
                            :model-value="file.progress"
                            height="4"
                            rounded
                            :color="file.status === 'error' ? 'error' : 'primary'"
                            />

                            <!-- File Size & Speed -->
                            <div class="d-flex justify-space-between mt-1">
                                <span class="text-caption text-medium-emphasis">
                                    {{ formatSize(file.uploadedSize) }} / {{ formatSize(file.size) }}
                                </span>

                                <span class="text-caption text-medium-emphasis">
                                    {{ file.speed }}
                                </span>
                            </div>
                        </div>
                    </div>
                </v-card-text>

                <!-- Actions -->
                <v-card-actions class="pa-4 border-t">
                    <v-spacer />
                    <v-btn 
                    v-if="!isUploading"
                    variant="text" 
                    @click="uploadDialogOpen = false"
                    >
                        Done
                    </v-btn>

                    <v-btn 
                    v-else
                    variant="text" 
                    color="error"
                    @click="cancelUpload"
                    >
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-app>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useStore } from 'vuex';
import { fileServerUrl } from '@/store/api';
import { toast } from "vue3-toastify";
import formattedDate from '@/plugins/dateFormatter';

import documentsMyDrive from '@/components/module/documents/myDriveManagement/documentsMyDrive.vue';
import documentsRecent from '@/components/module/documents/recentManagement/documentsRecent.vue';
import documentsStarred from '@/components/module/documents/starredManagement/documentsStarred.vue';
import documentsArchived from '@/components/module/documents/archivedManagement/documentsArchived.vue';
import documentsTrash from '@/components/module/documents/trashManagement/documentsTrash.vue';
import documentsShared from '@/components/module/documents/sharedMangement/documentsShared.vue';

export default {
    components: {
        documentsMyDrive,
        documentsRecent,
        documentsStarred,
        documentsArchived,
        documentsTrash,
        documentsShared
    },  
    data() {
        return {
            uploadAbortControllers: {}, // Track abort controllers for each file
            uploadDialogOpen: false,
            uploadingFiles: [], 
            totalFilesCount: 0,
            uploadedCount: 0,
            overallProgress: 0,
            timeLeftText: '',
            uploadSpeedText: '',
            uploadStartTime: null,
            uploadCancelToken: null,
            activeMenuBreadcrumb: "My Drive",
            activeMenu: "my-drive",
            sidebarListData: [
                {  title: "Home", value: 'home', icon: "mdi-home" },
                {  title: "My Drive", value: 'my-drive', icon: "mdi-tape-drive" },
                {  title: "Recent", value: 'recent', icon: "mdi-clock-time-four-outline" },
                {  title: "Starred", value: 'starred', icon: "mdi-star-outline" },
                {  title: "Shared", value: 'shared', icon: "mdi-share" },
                {  title: "Archive", value: 'archive', icon: "mdi-archive" },
                {  title: "Trash", value: 'trash', icon: "mdi-trash-can-outline" },
            ],
            permissionOptions: [
                { title: "Viewer", value: "view" },
                { title: "Editor", value: "edit" },
            ],  
            drawer: true,
            viewMode: "grid",
            searchQuery: "",
            sortBy: "Sort",
            sortOptions: [
                { title: "Sort", value: null },
                { title: "Name", value: "name" },
                { title: "Modified", value: "modified" },
                { title: "Size", value: "size" },
            ],
            folders: [],

            uploadProgress: 0,
            isUploading: false,
            isStarred: false,

            currentFolder: null,
            currentFolderId: null,
            selectedItems: new Set(),
            sharedWith: new Set(),
            dragOver: false,
            previewOpen: false,
            previewFile: null,
            folderContextMenu: false,
            fileContextMenu: false,
            folderContextTarget: null,
            fileContextTarget: null,
            menuX: 0,
            menuY: 0,
            sharedWithDialog: false,
            addUpdateDialog: false,
            renameValue: "",
            renameTarget: null,
            isUpdateMode: false,
            itemType: null,
            isSearchFieldExpanded: false,

            sharedUsersListLoading: false,
            usersToShareListLoading: false,
            sharedDialogTitle: '',
            sharedUsers: [],
            usersToShare: [],
        };
    },
    watch: {
        sharedWithDialog(val) {
            if (val === true) {
                this.shareDialogTitle();

                const datas = {
                    item_type: this.item_type.value.value,
                    item_id: this.item_id.value.value
                }

                this.usersToShareListLoading = true;
                this.sharedUsersListLoading = true;

                this.$store.dispatch("getSharedUsersList", datas)
                    .then(res => {
                        this.updateShareDialog(res);
                    });
            } else {
                this.sharedUsers = [];
                this.$store.commit("SET_SHARED_USERS_LIST");
            }
        },

        folderContextTarget(val) {
            console.log("folder context target",val)
        },

        fileContextTarget(val) {
            console.log("file context target",val)
        },

        activeMenu(val) {

            this.folders = [];
            if (val === 'recent') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Recent';
                if (this.$store.getters.getRecentDocumentsList.length === 0) {
                    this.$store.dispatch("getRecentDocumentsList");
                }
            }
            else if (val === 'home') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Home';
            }
            else if (val === 'starred') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Starred';
                this.$store.dispatch("getStarredDocumentsList");
            }
            else if (val === 'shared') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Shared';
                this.$store.dispatch("getSharedWithMeList");
            }
            else if (val === 'archive') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Archive';
                this.$store.dispatch("getArchivedDocumentsList");
            }
            else if (val === 'trash') {
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'Trash';
                this.$store.dispatch("getTrashDocumentsList");
            }else if (val === 'my-drive'){
                this.currentFolderId = null;
                this.activeMenuBreadcrumb = 'My Drive';
                this.$store.dispatch("getDocumentsFolderList")
                this.$store.dispatch("getDocumentsFileList");

            } 

        },
        currentFolderId(val) {
            this.parent_id.value.value = val;
        },
    },
    created() {
        if (this.$store.getters.getDocumentsFolderList.length === 0) {
            this.$store.dispatch("getDocumentsFolderList");
        }
        if (this.$store.getters.getDocumentsFileList.length === 0) {
            this.$store.dispatch("getDocumentsFileList");
        }
        if (this.$store.getters.getUsersList.length === 0) {
            this.$store.dispatch("getUsersList");
        }
    },  
    computed: {
        getSharedUsersList() {
            sharedUsersData = this.$store.getters.getSharedUsersList;
            return sharedUsersData;
        },

        getUsersList() {
            const users = this.$store.getters.getUsersList;
            return users;
        },
        getPermissionList() {
            return [
                { title: "Viewer", value: "view" },
                { title: "Editor", value: "edit" },
                { title: "Remove", value: "remove" }
            ]
        },

        isUploading() {
            return this?.uploadingFiles?.some(f => f.status === 'uploading');
        },  

        rootFolders() {
            return this.folders.filter((f) => f.parent === null);
        },

        breadcrumbs() {
            return [
                { title: this.activeMenuBreadcrumb, id: null, disabled: false },
                ...this.folders.map(folder => ({
                    title: folder.title || folder.name,
                    id: folder.id,
                    disabled: false
                }))
            ];
        },
        
        visibleFolders() {
            let folders = [];
            if (this.activeMenu === 'my-drive') {
                folders = this.$store.getters.getDocumentsFolderList
            } else if(this.activeMenu === 'starred'){
                folders = this.$store.getters.getStarredDocumentsList["folders"];
            } else if(this.activeMenu === 'archive'){
                folders = this.$store.getters.getArchivedDocumentsList["folders"];
            } else if(this.activeMenu === 'trash'){
                folders = this.$store.getters.getTrashDocumentsList["folders"];
            } else if(this.activeMenu === 'shared'){
                folders = this.$store.getters.getSharedWithMeList["folders"];
            } else {
                folders = [];
            } 
            return folders || [];
        },

        visibleFiles() {
            let files = [];
            if (this.activeMenu === 'my-drive') {
                files = this.$store.getters.getDocumentsFileList;
            } else if (this.activeMenu === 'recent') {
                files = this.$store.getters.getRecentDocumentsList;
            } else if (this.activeMenu === 'starred') {
                files = this.$store.getters.getStarredDocumentsList["files"];
            } else if (this.activeMenu === 'archive') {
                files = this.$store.getters.getArchivedDocumentsList["files"];
            } else if (this.activeMenu === 'trash') {
                files = this.$store.getters.getTrashDocumentsList["files"];
            } else if (this.activeMenu === 'shared') {
                files = this.$store.getters.getSharedWithMeList["file"];
            } else {
                files = [];
            }
            return files || [];
        },
        
        visibleItems() {
            return [...this.visibleFolders, ...this.visibleFiles];
        },
    },
    methods: {
        formattedDate,
        updateShareDialog(response) {
            this.sharedUsers = response;
            this.sharedUsersListLoading = false

            this.$store.dispatch("getUsersList")
                .then((res) => {

                    const usersList = res.data.data;
                    const sharedUserIds = this.sharedUsers.map(u => u.user_id);

                    this.usersToShare = usersList.filter(user =>
                        !sharedUserIds.includes(user.id)
                    );

                    this.usersToShareListLoading = false;
                }
            )
        },

        shareDialogTitle() {
            if (this.fileContextTarget) {
                const data = this.fileContextTarget;
                this.sharedDialogTitle = data?.original_name;
            } else {
                const data = this.folderContextTarget;
                this.sharedDialogTitle = data?.name;
            }
        },

        handleMenuChange(activeValue) {
            this.activeMenu = activeValue;
        },

        searchExpand() {
            this.isSearchFieldExpanded = !this.isSearchFieldExpanded;
        },

        getFileIcon(file) {
            const mimeType = file.mime_type || '';
            
            if (mimeType.startsWith('image/')) return 'mdi-image';
            if (mimeType.startsWith('video/')) return 'mdi-video';
            if (mimeType.startsWith('audio/')) return 'mdi-music';
            if (mimeType === 'application/pdf') return 'mdi-file-pdf-box';
            if (mimeType.includes('word') || mimeType.includes('document')) return 'mdi-file-word-box';
            if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'mdi-file-excel-box';
            if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'mdi-file-powerpoint-box';
            if (mimeType.includes('zip') || mimeType.includes('compressed') || mimeType.includes('archive')) return 'mdi-folder-zip';
            if (mimeType.includes('text') || mimeType === 'application/json') return 'mdi-file-document';
            
            return 'mdi-file';
        },

        getFileIconColor(file) {
            const mimeType = file.mime_type || '';
            
            if (mimeType.startsWith('image/')) return 'orange';
            if (mimeType.startsWith('video/')) return 'purple';
            if (mimeType.startsWith('audio/')) return 'green';
            if (mimeType === 'application/pdf') return 'red';
            if (mimeType.includes('word')) return 'blue';
            if (mimeType.includes('excel')) return 'teal';
            if (mimeType.includes('powerpoint')) return 'deep-orange';
            if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'amber';
            if (mimeType.includes('text')) return 'grey';
            
            return 'grey';
        },

        getFileTypeName(file) {
            const mimeType = file.mime_type || 'unknown';
            const parts = mimeType.split('/');
            
            if (parts.length === 2) {
                return `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)} (${parts[1].toUpperCase()})`;
            }
            
            return mimeType;
        },

        isImageFile(file) {
            return (file.mime_type || '').startsWith('image/');
        },

        isPdfFile(file) {
            return file.mime_type === 'application/pdf';
        },

        isDocumentFile(file) {
            const mimeType = file.mime_type || '';
            return mimeType.includes('word') || 
                   mimeType.includes('document') || 
                   mimeType.includes('text') ||
                   mimeType.includes('excel') ||
                   mimeType.includes('spreadsheet') ||
                   mimeType.includes('powerpoint') ||
                   mimeType.includes('presentation');
        },

        isVideoFile(file) {
            return (file.mime_type || '').startsWith('video/');
        },

        isAudioFile(file) {
            return (file.mime_type || '').startsWith('audio/');
        },

        getFilePreviewUrl(file) {
            // Construct the preview URL based on your API
            // Adjust the path according to your backend setup
            return fileServerUrl + file?.path;
        },

        formatFullDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        cancelUpload() {
            
            // Mark all uploading and queued files as cancelled
            this.uploadingFiles.forEach(file => {
                if (file.status === 'uploading' || file.status === 'queued') {
                    file.status = 'cancelled';
                    file.progress = 0;

                    // Abort the upload request for this file
                    if (this.uploadAbortControllers[file.id]) {
                        this.uploadAbortControllers[file.id].abort();
                        delete this.uploadAbortControllers[file.id];
                    }
                }
            });
            this.overallProgress = 0;
            this.totalFilesCount = 0;
            this.uploadedCount = 0;
            this.uploadDialogOpen = false;
            this.uploadingFiles = [];
            this.isUploading = false;
        },

        openFolder(folder) {
            this.folders.push({ title: folder.name, id: folder.id });
            this.currentFolderId = folder.id;

            if (this.activeMenu === 'starred') {
                this.$store.dispatch("getStarredDocumentsList", { parent_id: folder.id });
            } else if (this.activeMenu === 'archive') {
                this.$store.dispatch("getArchivedDocumentsList", { folder_id: folder.id });
            } else {
                this.$store.dispatch("getDocumentsFolderList", { parent_id: folder.id })
                this.$store.dispatch("getDocumentsFileList", { folder_id: folder.id });
            }
        },

        openFile(file) {
            this.previewFile = file;
            this.previewOpen = true;
        },

        handleItemClick(item) {
            if (item.original_name) {
                // It's a file
                this.openFile(item);
            } else {
                // It's a folder
                this.openFolder(item);
            }
        },

        handleBreadcrumbClick(item) {
            // If shared_with clicks "My Drive"
            // console.log("breadcrumb",item)
            if (item.id === null && item.title == "My Drive") {
                this.folders = [];
                this.currentFolderId = null;
                this.$store.dispatch("getDocumentsFolderList");
                this.$store.dispatch("getDocumentsFileList");
                return;
            } else if (item.title == "Recent") {
                this.currentFolderId = null;
                this.folders = [];
                this.$store.dispatch("getRecentDocumentsList");
                return;
            } else if (item.title == "Starred") {
                this.currentFolderId = null;
                this.folders = [];
                this.$store.dispatch("getStarredDocumentsList");
                return;
            } else if (item.title == "Archive") {
                this.currentFolderId = null;
                this.folders = [];
                this.$store.dispatch("getArchivedDocumentsList");
                return;
            } else if (item.title == "Trash") {
                this.currentFolderId = null;
                this.folders = [];
                this.$store.dispatch("getTrashDocumentsList");
                return;
            }

            // Remove breadcrumb items after the clicked one
            const index = this.folders.findIndex(f => f.id === item.id);
            this.folders = this.folders.slice(0, index + 1);

            // Navigate to that folder
            this.currentFolderId = item.id;

            if (this.activeMenu == "starred") {
                this.$store.dispatch("getStarredDocumentsList", { parent_id: item.id });
                return;
            } else if (this.activeMenu == "archive") {
                this.$store.dispatch("getArchivedDocumentsList", { folder_id: item.id });
                return;
            }

            this.$store.dispatch("getDocumentsFolderList", { parent_id: item.id });
            this.$store.dispatch("getDocumentsFileList", { folder_id: item.id });
        },

        toggleSelect(id, value) {
            const updated = new Set(this.selectedItems);

            if (value) {
                updated.add(id);
            } else {
                updated.delete(id);
            }

            this.selectedItems = updated;
        },

        showFolderContextMenu(event, folder) {
            this.folderContextTarget = folder;
            this.fileContextTarget = null;
            this.folder_id.value.value = folder.id;
            this.name.value.value = folder.name;
            this.menuX = event.clientX;
            this.menuY = event.clientY;
            this.folderContextMenu = false;
            this.fileContextMenu = false;
            this.folderContextMenu = true;
        },

        showFileContextMenu(event, file) {
            this.fileContextTarget = file;
            this.folderContextTarget = null;
            this.folder_id.value.value = file.id;
            this.name.value.value = file.original_name;
            this.menuX = event.clientX;
            this.menuY = event.clientY;
            this.folderContextMenu = false;
            this.fileContextMenu = false;
            this.fileContextMenu = true;
        },

        showListContextMenu(event, item) {
            if (item.original_name) {
                // It's a file
                this.showFileContextMenu(event, item);
            } else {
                // It's a folder
                this.showFolderContextMenu(event, item);
            }
        },

        handleFolderContextAction(action) {
            this.folderContextMenu = false;
            const item = this.folderContextTarget;
            if (!item) return;

            switch (action) {
                case "open":
                    this.openFolder(item);
                    break;
                case "rename":
                    this.addUpdateFolder(item);
                    break;
                case "download":
                    this.download(item, 'folder');
                    break;
                case "archive":
                    this.archiveItem(item, 'folder');
                    break;
                case "delete":
                    this.deleteItem(item, 'folder');
                    break;
                case "share":
                    this.shareItem(item, "folder");
                    break;
                case "starred":
                    this.setStarredItem(item, "folder");
                    break;
                case "restore":
                    this.restoreItem(item, "folder");
                    break;
                case "delete-forever":
                    this.deleteForever(item, "folder");
                    break;
            }
        },
        
        handleFileContextAction(action) {
            this.fileContextMenu = false;
            const item = this.fileContextTarget;
            if (!item) return;

            switch (action) {
                case "preview":
                    this.openFile(item);
                    break;
                case "download":
                    this.download(item, 'file');
                    break;
                case "delete":
                    this.deleteItem(item, 'file');
                    break;
                case "share":
                    this.shareItem(item, "file");
                    break;
                case "archive":
                    this.archiveItem(item, "file");
                    break;
                case "starred":
                    this.setStarredItem(item, "file");
                    break;
                case "restore":
                    this.restoreItem(item, "file");
                    break;
                case "delete-forever":
                    this.deleteForever(item, "file");
                    break;
            }
        },

        addUpdateFolder(item) {
            this.itemType = 'folder';
            if (item.id) {
                this.isUpdateMode = true;
                this.renameTarget = item;
                this.name.value.value = item.name;
                this.folder_id.value.value = item.id;
            } else {
                this.isUpdateMode = false;
                this.renameTarget = null;
                this.name.value.value = '';
                this.folder_id.value.value = '';
            }
            this.addUpdateDialog = true;
        },

        addUpdateFile(item) {
            this.isUpdateMode = true;
            this.itemType = 'file';
            this.renameTarget = item;
            this.name.value.value = item.original_name;
            this.folder_id.value.value = item.id;
            this.addUpdateDialog = true;
        },

        confirmSaveUpdate() {
            if (!this.name.value.value) return;
            
            if (this.renameTarget) {
                this.renameTarget.name = this.name.value.value;
            }
            
            this.addUpdateDialog = false;
            this.submit();
        },

        deleteItem(item, type) {
            if (type === 'file') {   
                this.$store.dispatch("deleteFile", item.id)
                .then(() => {
                    this.previewOpen = false;
                    this.$store.dispatch("getDocumentsFolderList", { parent_id: this.currentFolderId})
                    this.$store.dispatch("getDocumentsFileList", { folder_id: this.currentFolderId})
                    this.$store.dispatch("getRecentDocumentsList")
                })
            } else {
                this.$store.dispatch("deleteFolder", item.id)
                .then(() => {
                    this.previewOpen = false;
                    this.$store.dispatch("getDocumentsFolderList", { parent_id: this.currentFolderId})
                    this.$store.dispatch("getDocumentsFileList", { folder_id: this.currentFolderId})
                }) 
            }
        },

        setStarredItem(data, type) {
            this.item_type.value.value = type;
            this.item_id.value.value = data.id;

            this.starredSubmit();
        },

        restoreItem(data, type) {
            this.item_type.value.value = type;
            this.item_id.value.value = data.id;

            this.restoreSubmit();            
        },

        deleteForever(data, type) {
            this.item_type.value.value = type;
            this.item_id.value.value = data.id;

            this.deleteForeverSubmit();            
        },

        archiveItem(data, type) {
            this.item_type.value.value = type;
            this.item_id.value.value = data.id;

            this.archiveSubmit();
        },

        shareItem(data, type) {
            this.sharedWithDialog = true;
            this.item_type.value.value = type;
            this.item_id.value.value = data.id;

        },
        // Replace your download method with this:

        download(item, type) {
            let downloadUrl;

            if (type === 'file') {
                downloadUrl =  `http://127.0.0.1:8000/api/auth/document/files/${item.id}/download`;   
            } else {
                downloadUrl =  `http://127.0.0.1:8000/api/auth/document/folders/${item.id}/download`;   
            }
            
            // Get your auth token (adjust based on where you store it)
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            
            if (!token) {
                alert('Authentication token not found. Please log in again.');
                return;
            }

            // Show loading state (optional)
            const originalText = 'Downloading...';
            
            fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': '*/*'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.blob();
            })
            .then(blob => {
                // Create a temporary URL for the blob
                const url = window.URL.createObjectURL(blob);
                
                // Create a temporary anchor element
                const link = document.createElement('a');
                link.href = url;

                let filename;

                if (type === 'folder') {
                    filename = item.name || `${type}_${item.id}`;
                } else {
                    filename = item.original_name || `${type}_${item.id}`;
                }
                link.setAttribute('download', filename);
                
                // Append to body, click, and remove
                document.body.appendChild(link);
                link.click();
                
                // Cleanup
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);
            })
            .catch(error => {
                console.error('Download failed:', error);
                alert('Failed to download file. Please try again.');
            });
        },  

        triggerFileUpload() {
            this.$refs.fileInput.click();
        },
        
        handleMultipleUpload(event) {
            const files = event.target.files;
            
            if (!files || files.length === 0) {
                return;
            }

            this.uploadMultipleFiles(files);
            
            // Reset input so same files can be uploaded again if needed
            event.target.value = '';
        },
        
        uploadMultipleFiles(files) {
            // Initialize upload tracking for new files
            const newFiles = Array.from(files).map((file, index) => ({
                id: `${Date.now()}-${index}`,
                name: file.name,
                size: file.size,
                progress: 0,
                uploadedSize: 0,
                speed: '0 KB/s',
                status: 'queued', // Changed from 'uploading' to 'queued'
                file: file
            }));

            // Append new files to existing queue instead of replacing
            this.uploadingFiles.push(...newFiles);

            // Only initialize upload tracking if this is the first batch
            if (this.uploadingFiles.length === newFiles.length) {
                this.totalFilesCount = this.uploadingFiles.length;
                this.uploadedCount = 0;
                this.overallProgress = 0;
                this.uploadStartTime = Date.now();
            } else {
                // Update total count to include new files
                this.totalFilesCount = this.uploadingFiles.length;
            }
            this.activeMenu = 'my-drive';
            this.uploadDialogOpen = true;

            // Only start uploading if no files are currently being uploaded
            if (!this.isUploading) {
                this.uploadFilesSequentially();
            }
        },

        uploadFilesSequentially() {
            // Check if all uploads are cancelled
            const hasCancelled = this.uploadingFiles.some(f => f.status === 'cancelled');
            const hasQueued = this.uploadingFiles.some(f => f.status === 'queued');
            
            // If any are cancelled and no queued files, stop
            if (hasCancelled && !hasQueued) {
                this.isUploading = false;
                return;
            }

            // Get the first queued file
            const fileToUpload = this.uploadingFiles.find(f => f.status === 'queued');
            
            if (!fileToUpload) {
                // No more queued files
                const uploadingFiles = this.uploadingFiles.filter(f => f.status === 'uploading');
                if (uploadingFiles.length === 0) {
                    this.isUploading = false;
                }
                return;
            }

            // Mark file as uploading
            fileToUpload.status = 'uploading';
            this.uploadSingleFile(fileToUpload);
        },

        uploadSingleFile(fileItem) {
            // Check if this file was cancelled before starting
            if (fileItem.status === 'cancelled') {
                this.uploadFilesSequentially();
                return;
            }

            const formData = new FormData();

            if (this.currentFolderId) {
                formData.append("folder_id", this.currentFolderId);
                formData.append("parent_id", this.currentFolderId);
            }

            formData.append("file[]", fileItem.file);

            // Create abort controller for this file
            const abortController = new AbortController();
            this.uploadAbortControllers[fileItem.id] = abortController;

            // Track upload progress for this file
            const fileStartTime = Date.now();
            const fileStartSize = 0;

            this.$store.dispatch("saveDocumentsFile", {
                formData,
                signal: abortController.signal, // Pass the abort signal
                onProgress: (percent) => {
                    // Check if cancelled before updating progress
                    if (fileItem.status === 'cancelled') {
                        return;
                    }

                    // Update individual file progress
                    fileItem.progress = percent;
                    fileItem.uploadedSize = (percent / 100) * fileItem.size;

                    // Calculate speed
                    const elapsedSeconds = (Date.now() - fileStartTime) / 1000;
                    const uploadedBytes = fileItem.uploadedSize - fileStartSize;
                    const speedBytesPerSecond = uploadedBytes / (elapsedSeconds || 1);
                    fileItem.speed = this.formatSpeed(speedBytesPerSecond);

                    // Update overall progress
                    this.updateOverallProgress();
                    this.updateTimeLeft();
                }
            })
            .then(() => {
                // Check if cancelled before marking as completed
                if (fileItem.status === 'cancelled') {
                    delete this.uploadAbortControllers[fileItem.id];
                    this.uploadFilesSequentially();
                    return;
                }

                // File completed
                fileItem.status = 'completed';
                fileItem.progress = 100;
                this.uploadedCount++;
                this.updateOverallProgress();

                // Refresh file list
                this.$store.dispatch("getDocumentsFolderList", { parent_id: this.currentFolderId })
                this.$store.dispatch("getDocumentsFileList", { folder_id: this.currentFolderId })
                this.$store.dispatch("getRecentDocumentsList")
                
                // Clean up abort controller
                delete this.uploadAbortControllers[fileItem.id];
                
                // Upload next file
                setTimeout(() => {
                    this.uploadFilesSequentially();
                }, 300);
            })
            .catch((error) => {
                // Check if error is due to cancellation
                if (error.name === 'AbortError' || fileItem.status === 'cancelled') {
                    console.log('Upload cancelled:', fileItem.name);
                    fileItem.status = 'cancelled';
                    fileItem.progress = 0;
                } else {
                    console.error('Upload error:', error);
                    fileItem.status = 'error';
                    fileItem.progress = 0;
                }

                // Clean up abort controller
                delete this.uploadAbortControllers[fileItem.id];

                // Continue with next file only if not cancelled
                if (fileItem.status !== 'cancelled') {
                    setTimeout(() => {
                        this.uploadFilesSequentially();
                    }, 300);
                } else {
                    // If cancelled, stop the upload queue
                    this.isUploading = false;
                }
            });
        },

        updateOverallProgress() {
            const totalSize = this.uploadingFiles.reduce((sum, f) => sum + f.size, 0);
            const uploadedSize = this.uploadingFiles.reduce((sum, f) => sum + f.uploadedSize, 0);
            this.overallProgress = totalSize > 0 ? (uploadedSize / totalSize) * 100 : 0;
        },

        updateTimeLeft() {
            if (this.overallProgress === 0) return;

            const elapsedMs = Date.now() - this.uploadStartTime;
            const totalSize = this.uploadingFiles.reduce((sum, f) => sum + f.size, 0);
            const uploadedSize = this.uploadingFiles.reduce((sum, f) => sum + f.uploadedSize, 0);
            const remainingSize = totalSize - uploadedSize;

            if (uploadedSize === 0) return;

            const avgSpeedBytesPerMs = uploadedSize / elapsedMs;
            const timeLeftMs = remainingSize / avgSpeedBytesPerMs;
            const timeLeftSeconds = Math.round(timeLeftMs / 1000);

            if (timeLeftSeconds < 60) {
                this.timeLeftText = `${timeLeftSeconds}s left`;
            } else {
                const minutes = Math.round(timeLeftSeconds / 60);
                this.timeLeftText = `${minutes}m left`;
            }

            // Calculate upload speed
            const totalSpeedBytesPerSecond = uploadedSize / (elapsedMs / 1000);
            this.uploadSpeedText = this.formatSpeed(totalSpeedBytesPerSecond);
        },

        formatSpeed(bytesPerSecond) {
            if (bytesPerSecond >= 1024 * 1024) {
                return (bytesPerSecond / (1024 * 1024)).toFixed(2) + ' MB/s';
            }
            if (bytesPerSecond >= 1024) {
                return (bytesPerSecond / 1024).toFixed(2) + ' KB/s';
            }
            return bytesPerSecond.toFixed(0) + ' B/s';
        },

        handleDrop(event) {
            this.dragOver = false;
            const files = event.dataTransfer.files;
            
            if (!files || files.length === 0) {
                console.log('No files dropped');
                return;
            }
            // This will now append to the queue instead of replacing
            this.uploadMultipleFiles(files);
        },

        formatSize(bytes) {
            if (bytes >= 1e9) return (bytes / 1e9).toFixed(2) + " GB";
            if (bytes >= 1e6) return (bytes / 1e6).toFixed(2) + " MB";
            if (bytes >= 1e3) return (bytes / 1e3).toFixed(1) + " KB";
            return bytes + " B";
        },
    },
    setup() {
        const { proxy } = getCurrentInstance();
        const store = useStore();
        const { handleSubmit, resetForm } = useForm({
            //validations
        })

        //starred

        //shared 
        const item_type = useField('item_type');
        const item_id = useField('item_id');
        const shared_with = useField('shared_with');
        const permission = useField('permission');
        permission.value.value = "edit";

        //initial
        const folder_id = useField('folder_id');
        const parent_id = useField('parent_id');
        const name = useField('name');
        const submit = handleSubmit((values) => {
            if (values.folder_id) {
                store
                .dispatch("updateDocumentFolder", values)
                .then(() => {
                    resetForm();
                    return store.dispatch("getDocumentsFolderList", { parent_id: values.parent_id });
                })
                .catch((err) => {
                    console.error("Save failed:", err);
                });
            } else {
                store
                .dispatch("saveDocumentsFolder", values)
                .then(() => {
                    resetForm();
                    proxy.activeMenu = 'my-drive';
                    return store.dispatch("getDocumentsFolderList", { parent_id: values.parent_id });
                })
                .catch((err) => {
                    console.error("Save failed:", err);
                });
            }

        })

        const shareWithSubmit = handleSubmit((values) => {
            const sharedData = []

            if (values.shared_with) {  
                values.shared_with.forEach((item) => {
                    sharedData.push({
                        item_type: values.item_type,
                        item_id: values.item_id,
                        permission: values.permission,
                        shared_with: item,
                    })
                })
            }

            if (proxy.sharedUsers) {
                proxy.sharedUsers.forEach(item => {
                    if (item.permission === 'remove') {

                        const data = {
                            item_type: values.item_type,
                            item_id: values.item_id,
                            permission: null,
                            shared_with: null,
                        }

                        sharedData.push(data);

                    } else {
                        
                        sharedData.push({
                            item_type: values.item_type,
                            item_id: values.item_id,
                            permission: item.permission,
                            shared_with: item.user_id,
                        });

                    }

                });
            }

            store.dispatch("shareDocuments", { items: sharedData }).then(res => {
                toast.success(res.message);
                proxy.sharedWithDialog = false;
                proxy.usersToShare = [];
                shared_with.value.value = null;
            })
        })

        const starredSubmit = handleSubmit((values) => {
            const starredData = {
                item_type: values.item_type,
                item_id: values.item_id,
            }

            store.dispatch("saveStarredDocuments", starredData)
                .then(() => {
                    store.dispatch("getStarredDocumentsList")
                    store.dispatch("getDocumentsFolderList", { parent_id: proxy.currentFolderId })
                    store.dispatch("getDocumentsFileList", { folder_id: proxy.currentFolderId });
                    values.item_type = '',
                    values.item_id = null
                });
        })

        const archiveSubmit = handleSubmit((values) => {
            const archiveData = {
                item_type: values.item_type,
                item_id: values.item_id
            }

            let archiveAction;

            if (proxy.fileContextTarget) { 
                if (proxy.fileContextTarget.is_archived === 1) {
                    archiveAction = "restoreArchivedDocument";
                } else {
                    archiveAction = "saveArchiveDocument";
                }
            } else {
                if (proxy.folderContextTarget.is_archived === 1) {
                    archiveAction = "restoreArchivedDocument";
                } else {
                    archiveAction = "saveArchiveDocument";
                }
            }

            store.dispatch(archiveAction, archiveData)
                .then(() => {
                    store.dispatch("getArchivedDocumentsList")
                    store.dispatch("getDocumentsFolderList", { parent_id: proxy.currentFolderId })
                    store.dispatch("getDocumentsFileList", { folder_id: proxy.currentFolderId });
                    values.item_type = '',
                    values.item_id = null
                });
        })

        const restoreSubmit = handleSubmit((values) => {
            const restoreData = {
                item_type: values.item_type,
                item_id: values.item_id,
            }

            store.dispatch("restoreTrashDocument", restoreData)
                .then(() => {
                    store.dispatch("getTrashDocumentsList")
                    values.item_type = '',
                    values.item_id = null
                });
        })

        const deleteForeverSubmit = handleSubmit((values) => {
            const deleteForeverData = {
                item_type: values.item_type,
                item_id: values.item_id,
            }

            store.dispatch("forceDeleteTrashDocument", deleteForeverData)
                .then(() => {
                    store.dispatch("getTrashDocumentsList")
                    values.item_type = '',
                    values.item_id = null
                });
        })

        return {
            submit,
            folder_id,
            parent_id,
            name,
            shared_with,
            item_type,
            item_id,
            permission,
            shareWithSubmit,
            starredSubmit,
            archiveSubmit,
            restoreSubmit,
            deleteForeverSubmit
        }
    },
    mounted() {

    }
};
</script>

<style scoped>
    
.border-dashed {
    border-style: dashed !important;
}

.cursor-pointer {
    cursor: pointer;
}

.folder-card {
    display: flex;
    height: 45px;
    align-items: center;
    padding: 4px 10px;
}

.file-item-container {
    position: relative;
}

.file-card {
    border-radius: 8px;
    transition: all 0.2s ease;
    overflow: hidden;
}

.file-thumbnail {
    width: 100%;
    height: 150px;
    background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
}

.file-preview-image {
    width: 100%;
    height: 100%;
}

.file-icon-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.file-name {
    font-size: 0.875rem;
    line-height: 1.2;
    max-height: 2.4em;
}

.file-menu-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.file-menu-btn .v-btn {
    background: rgba(255, 255, 255, 0.9) !important;
}

.section-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: #5f6368;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.search-field {
    transition: max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-field :deep(.v-field) {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
/* Search Field */
.search-field :deep(.v-field__input) {
    min-height: 35px !important;
    height: 35px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 15px !important;
}

.permission-select {
  width: 120px;
}


.search-field :deep(.v-field) {
    --v-input-control-height: 35px;
}

.search-field :deep(.v-field__prepend-inner),
.search-field :deep(.v-field__append-inner) {
    height: 35px !important;
}

/* Sort Dropdown */
.small-input :deep(.v-field__input) {
    min-height: 35px !important;
    height: 35px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 15px !important;
}

.small-input :deep(.v-field) {
    --v-input-control-height: 35px;
}

.small-input :deep(.v-field__prepend-inner),
.small-input :deep(.v-field__append-inner) {
    height: 35px !important;
}

/* View Toggle */
.view-toggle-small :deep(.v-btn-toggle) {
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 !important;
}

.view-toggle-small :deep(.v-btn) {
    margin-top: 7px !important;
    height: 34px !important;
    min-height: 34px !important;
    width: 34px !important;
    padding: 0 !important;
}

.view-toggle-small :deep(.v-icon) {
    font-size: 18px !important;
}

/* Position dialog at bottom-right */
.upload-dialog-bottom-right {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
}

.upload-dialog-bottom-right :deep(.v-dialog__content) {
    align-items: flex-end !important;
    justify-content: flex-end !important;
}

.upload-dialog-bottom-right :deep(.v-overlay__content) {
    margin: 0 !important;
    margin-bottom: 10px !important;
    margin-right: 15px !important;
    align-self: flex-end !important;
}
</style>